import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSetEnterprisePrice } from './api/useSetEnterprisePrice';
import { useGetOrg } from './api/useGetOrg';
import { Env, getAdminApiUrl, ENV_COLORS, Spinner, ErrorBanner, SuccessBanner } from '../shared';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function formatCents(cents: number): string {
  return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(cents / 100);
}

/** Returns positive integer cents, or null if the dollar string isn't a valid positive amount. */
function dollarsToCents(dollars: string): number | null {
  const trimmed = dollars.trim();
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return null;
  const cents = Math.round(parseFloat(trimmed) * 100);
  return cents > 0 ? cents : null;
}

type PendingSave = { kind: 'set'; cents: number } | { kind: 'clear' };

// ─────────────────────────────────────────────────────────────────────────────
// Confirm Modal
// ─────────────────────────────────────────────────────────────────────────────

function SaveConfirmModal({
  pending,
  orgId,
  onClose,
  onConfirm,
  loading,
}: {
  pending: PendingSave;
  orgId: string;
  onClose: () => void;
  onConfirm: () => void;
  loading: boolean;
}) {
  const isClear = pending.kind === 'clear';
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-gray-900 border border-gray-700 rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-sm flex flex-col max-h-[92vh] sm:my-4">
        <div className="px-5 sm:px-6 py-5 sm:py-6 space-y-4 flex-1 overflow-y-auto">
          <div className="w-12 h-12 mx-auto rounded-xl bg-violet-950/50 border border-violet-800/50 flex items-center justify-center">
            <svg className="w-6 h-6 text-violet-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="text-center">
            <h3 className="text-lg font-semibold text-white">
              {isClear ? 'Clear Enterprise price' : 'Set Enterprise price'}
            </h3>
            <p className="text-sm text-gray-400 mt-1">
              {isClear ? (
                <>
                  Clear the Enterprise price for this org? It returns to{' '}
                  <span className="text-white font-medium">not yet priced</span> and can no longer
                  subscribe until a price is set again.
                </>
              ) : (
                <>
                  Set this org&apos;s Enterprise price to{' '}
                  <span className="text-white font-medium">{formatCents(pending.cents)}/mo</span>? This
                  unblocks the org&apos;s ability to subscribe and be billed.
                </>
              )}
            </p>
            <p className="text-xs text-gray-600 mt-2 font-mono break-all">{orgId}</p>
          </div>
        </div>
        <div className="flex gap-3 px-5 sm:px-6 py-4 border-t border-gray-800 flex-shrink-0">
          <button
            onClick={onClose}
            disabled={loading}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-300 bg-gray-800 hover:bg-gray-700 rounded-xl transition-all disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className={`flex-1 px-4 py-2.5 text-sm font-medium text-white rounded-xl transition-all disabled:opacity-50 flex items-center justify-center gap-2 ${
              isClear ? 'bg-red-600 hover:bg-red-700' : 'bg-violet-600 hover:bg-violet-700'
            }`}
          >
            {loading ? (
              <>
                <Spinner size="sm" />
                Saving…
              </>
            ) : isClear ? (
              'Clear price'
            ) : (
              'Set price'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Set Org Enterprise Price Tab
// ─────────────────────────────────────────────────────────────────────────────

export function EnterprisePricingTab() {
  const [selectedEnv, setSelectedEnv] = useState<Env>('prod');
  const [orgId, setOrgId] = useState('');
  const [dollars, setDollars] = useState('');
  const [pending, setPending] = useState<PendingSave | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  // Debounced id feeds the lookup so we don't fetch on every keystroke.
  const [debouncedOrgId, setDebouncedOrgId] = useState('');

  const { loading, setPrice } = useSetEnterprisePrice();

  const orgIdValid = UUID_RE.test(orgId.trim());
  const cents = useMemo(() => dollarsToCents(dollars), [dollars]);
  const amountValid = cents !== null;

  useEffect(() => {
    const t = setTimeout(() => setDebouncedOrgId(orgId.trim()), 400);
    return () => clearTimeout(t);
  }, [orgId]);

  // Look up the org's current price once the id is a valid UUID (GET is gone
  // to as soon as it settles; invalid/empty resets to idle).
  const lookupReady = UUID_RE.test(debouncedOrgId);
  const { org, status: orgStatus, error: orgError, applyPrice } = useGetOrg(
    selectedEnv,
    debouncedOrgId,
    lookupReady,
  );

  const beginSet = () => {
    setErrorMsg(null);
    if (!orgIdValid) return setErrorMsg('Enter a valid organization ID (UUID).');
    if (!amountValid) return setErrorMsg('Enter a positive dollar amount (up to 2 decimal places).');
    setPending({ kind: 'set', cents: cents! });
  };

  const beginClear = () => {
    setErrorMsg(null);
    if (!orgIdValid) return setErrorMsg('Enter a valid organization ID (UUID).');
    setPending({ kind: 'clear' });
  };

  const handleConfirm = useCallback(async () => {
    if (!pending) return;
    const id = orgId.trim();
    const outcome = await setPrice(selectedEnv, id, pending.kind === 'clear' ? null : pending.cents);
    if (outcome.ok) {
      applyPrice(outcome.enterpriseMonthlyCents);
      setSuccessMsg(
        outcome.enterpriseMonthlyCents === null
          ? 'Enterprise price cleared — org is now not yet priced.'
          : `Enterprise price set to ${formatCents(outcome.enterpriseMonthlyCents)}/mo.`,
      );
      setPending(null);
      setErrorMsg(null);
      if (pending.kind === 'clear') setDollars('');
      setTimeout(() => setSuccessMsg(null), 4000);
    } else {
      setErrorMsg(outcome.message);
      setPending(null);
    }
  }, [pending, orgId, selectedEnv, setPrice, applyPrice]);

  return (
    <>
      {/* Toolbar: heading + env switcher */}
      <div className="flex items-center gap-2 sm:gap-4 mb-6 flex-wrap">
        <div className="flex-1 min-w-[160px]">
          <h2 className="text-sm font-semibold text-gray-300">Set org Enterprise price</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Custom per-org monthly price from direct sales. Setting a price unblocks the org&apos;s
            ability to subscribe; clearing returns it to not-yet-priced.
          </p>
        </div>

        <div className="flex items-center gap-1.5 p-1 bg-gray-800/80 border border-gray-700 rounded-xl">
          {(['dev', 'staging', 'prod'] as Env[]).map(env => {
            const isConfigured = Boolean(getAdminApiUrl(env));
            const isActive = selectedEnv === env;
            return (
              <button
                key={env}
                onClick={() => isConfigured && setSelectedEnv(env)}
                title={isConfigured ? undefined : 'Not configured'}
                disabled={!isConfigured}
                className={`px-3 py-1 text-xs font-medium rounded-lg border transition-all capitalize ${
                  isActive
                    ? `${ENV_COLORS[env].pill} border-opacity-100`
                    : isConfigured
                      ? 'text-gray-400 border-transparent hover:text-gray-200 hover:bg-gray-700/60'
                      : 'text-gray-600 border-transparent cursor-not-allowed opacity-40'
                }`}
              >
                {env}
              </button>
            );
          })}
        </div>

        {selectedEnv === 'prod' && (
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs font-semibold tracking-wide ${ENV_COLORS.prod.badge}`}>
            <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse" />
            PRODUCTION
          </span>
        )}
      </div>

      {successMsg && <SuccessBanner message={successMsg} />}
      {errorMsg && <div className="mb-4"><ErrorBanner message={errorMsg} /></div>}

      <div className="max-w-lg bg-gray-900 border border-gray-800 rounded-2xl p-5 sm:p-6 space-y-5">
        {/* Org ID */}
        <div>
          <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
            Organization ID
          </label>
          <input
            type="text"
            value={orgId}
            onChange={e => setOrgId(e.target.value)}
            placeholder="00000000-0000-0000-0000-000000000000"
            spellCheck={false}
            className={`w-full px-4 py-2.5 bg-gray-800 border rounded-xl text-sm text-white placeholder-gray-600 font-mono focus:outline-none focus:ring-1 transition-all ${
              orgId && !orgIdValid
                ? 'border-red-700 focus:border-red-500 focus:ring-red-500'
                : 'border-gray-700 focus:border-violet-500 focus:ring-violet-500'
            }`}
          />
          {orgId && !orgIdValid && (
            <p className="text-xs text-red-400 mt-1.5">Not a valid UUID.</p>
          )}

          {/* Live lookup of the org's current price. */}
          {orgIdValid && (
            <div className="mt-3">
              {orgStatus === 'loading' && (
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <Spinner size="sm" />
                  Looking up organization…
                </div>
              )}
              {orgStatus === 'not_found' && (
                <p className="text-xs text-amber-400">No organization found with that ID.</p>
              )}
              {orgStatus === 'error' && orgError && (
                <p className="text-xs text-red-400">{orgError}</p>
              )}
              {orgStatus === 'found' && org && (
                <div className="rounded-xl bg-gray-800/60 border border-gray-700 px-4 py-3">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm font-medium text-white truncate">{org.name}</p>
                    {org.orgTierActive && (
                      <span className="flex-shrink-0 px-2 py-0.5 rounded-md bg-emerald-950/60 border border-emerald-800/50 text-[11px] font-medium text-emerald-300">
                        Subscribed
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    Current price:{' '}
                    {org.enterpriseMonthlyCents === null ? (
                      <span className="text-amber-400 font-medium">not yet priced</span>
                    ) : (
                      <span className="text-emerald-400 font-medium tabular-nums">
                        {formatCents(org.enterpriseMonthlyCents)}/mo
                      </span>
                    )}
                  </p>
                  {org.orgTierActive && (
                    <p className="text-[11px] text-gray-500 mt-1">
                      Already subscribed — changing the amount does not re-bill the live subscription.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Amount */}
        <div>
          <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
            Monthly price (USD)
          </label>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
            <input
              type="text"
              inputMode="decimal"
              value={dollars}
              onChange={e => setDollars(e.target.value)}
              placeholder="0.00"
              className={`w-full pl-8 pr-16 py-2.5 bg-gray-800 border rounded-xl text-sm text-white placeholder-gray-600 tabular-nums focus:outline-none focus:ring-1 transition-all ${
                dollars && !amountValid
                  ? 'border-red-700 focus:border-red-500 focus:ring-red-500'
                  : 'border-gray-700 focus:border-violet-500 focus:ring-violet-500'
              }`}
            />
            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 text-xs">/ month</span>
          </div>
          {dollars && !amountValid && (
            <p className="text-xs text-red-400 mt-1.5">Enter a positive amount, up to 2 decimals.</p>
          )}
          {amountValid && (
            <p className="text-xs text-gray-500 mt-1.5 tabular-nums">= {cents!.toLocaleString()} cents</p>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-1">
          <button
            onClick={beginSet}
            disabled={loading || !orgIdValid || !amountValid || orgStatus === 'not_found'}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-violet-600 hover:bg-violet-700 rounded-xl transition-all disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Set price
          </button>
          <button
            onClick={beginClear}
            disabled={loading || !orgIdValid || orgStatus === 'not_found'}
            className="px-4 py-2.5 text-sm font-medium text-red-300 bg-red-950/30 border border-red-800/50 rounded-xl hover:text-white hover:border-red-500 hover:bg-red-900/40 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Clear
          </button>
        </div>
      </div>

      {pending && (
        <SaveConfirmModal
          pending={pending}
          orgId={orgId.trim()}
          onClose={() => setPending(null)}
          onConfirm={handleConfirm}
          loading={loading}
        />
      )}
    </>
  );
}
